
import Vue, { PropType } from 'vue';
import Api from '@/core/api';
import { get } from 'lodash';
import Question from './Question.vue';
import NoQuestion from './NoQuestion.vue';
import QnaSortingPicker from './QnaSortingPicker.vue';
import { ORIENTATION_OPTIONS, Sorting, SortOption } from './sorting';
import { Theme } from '@/side-bar/side-bar-config.interface';

/**
 * @constant {number} MAX_QUESTIONS_PER_THROTTLE the maximum number of unmoderated questions a user can do in the given throttle time
 * @private
 */
const MAX_QUESTIONS_PER_THROTTLE = 2;

/**
 * @constant {number} THROTTLE_TIME the time in seconds to wait until user can send more than `MAX_QUESTIONS_PER_THROTTLE` unmoderated questions
 * @private
 */
const THROTTLE_TIME = 60;

const POST_NOTIFICATION_TIMEOUT = 5000;

export interface Question {
    id: string,
    question: string,
    status: string,
    upvoted: boolean,
    upvotes: number,
    userName: string,
    userId: string,
    profilePicture: string,
    sent: string
}

export default Vue.extend({
    name: 'Qna',

    components: {
        NoQuestion,
        Question,
        QnaSortingPicker
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        },
        config: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        },
        theme: {
            type: Object as PropType<Theme>,
            default: () => ({})
        },
        isPrivateQna: {
            type: Boolean,
            default: () => false
        }
    },

    data() {
        return {
            newQuestion: '',
            sendAnonymously: false,
            nrQuestionsAllowed: MAX_QUESTIONS_PER_THROTTLE,
            throttleTime: THROTTLE_TIME,
            showQnaLimitNotification: false,
            showPostNotification: false,
            sending: false,
            notificationTimeout: 0,
            throttleInterval: 0,
            timerTicker: 0,
            selectedOrientation: ''
        };
    },

    computed: {
        upvotingEnabled(): boolean {
            return get(this, 'config.upvotingEnabled', false);
        },
        upvotingAllowed(): boolean {
            return get(this, 'config.upvotingAllowed', false);
        },
        sorting(): Sorting | undefined {
            const sorting = get(this, 'config.sorting');

            if (!sorting) {
                return undefined;
            }

            const labels = get(this, 'labels.sortOptions', {}) as Record<string, string>;
            const options: SortOption[] = sorting.options.map((option : SortOption) => ({
                defaultOrientation: option.defaultOrientation || ORIENTATION_OPTIONS.BOTTOM_TO_TOP,
                label: labels[option.id],
                id: option.id,
                showOrientationToggler: option.showOrientationToggler || false
            })).filter((option: SortOption) => option.label && option.id);

            return {
                ...sorting,
                options,
                selectedOption: options.find(option => option.id === sorting.sortBy) ?? options[0]
            };
        },
        align(): string {
            if (this.orientation === ORIENTATION_OPTIONS.TOP_TO_BOTTOM) {
                return 'top';
            }
            return 'bottom';
        },
        orientation(): string {
            if (!this.sorting) {
                return ORIENTATION_OPTIONS.BOTTOM_TO_TOP;
            }
            if (this.sorting.selectedOption?.showOrientationToggler && this.selectedOrientation) {
                return this.selectedOrientation;
            }
            return this.sorting.selectedOption?.defaultOrientation;
        },
        questions(): Question[] {
            const questions = get(this, 'config.questions', []);

            const qnaContent = this.$refs.qnaContent as HTMLCanvasElement;
            const isScrolledAtTop = qnaContent?.scrollHeight - qnaContent?.offsetHeight === qnaContent?.scrollTop;
            const isScrolledAtBottom = qnaContent?.scrollTop < 1 && qnaContent?.scrollTop > -1;

            if (!this.sorting || this.sorting?.sortBy === 'newest') {
                if (this.orientation === ORIENTATION_OPTIONS.BOTTOM_TO_TOP && isScrolledAtBottom) {
                    qnaContent.scrollTop = 0;
                }
                if (this.orientation === ORIENTATION_OPTIONS.TOP_TO_BOTTOM && isScrolledAtTop) {
                    qnaContent.scrollTop = qnaContent.scrollHeight;
                }
                if (this.orientation === ORIENTATION_OPTIONS.TOP_TO_BOTTOM) {
                    return [...questions].reverse();
                }
            }

            return questions;
        },
        highlightedQuestion(): Question {
            return get(this, 'config.highlightedQuestion');
        },
        anonymousAllowed():boolean {
            return get(this, 'config.anonymousAllowed', false);
        },
        hidden():boolean {
            return get(this, 'config.hidden', false);
        },
        throttleAskingQuestions():boolean {
            return get(this, 'config.throttleAskingQuestions', false);
        },
        qnaLimitMessage():string {
            return get(this, 'labels.qnaLimitNotification', '').replace('{0}', this.throttleTime);
        },
        showHighlighted(): boolean {
            return !!this.highlightedQuestion;
        },
        disableSending(): boolean {
            return get(this, 'config.disableSendingQuestions', false);
        }
    },

    watch: {
        throttleAskingQuestions: {
            immediate: true,
            handler() {
                if (this.throttleAskingQuestions) {
                    this.initThrottling();
                } else {
                    this.cancelThrottling();
                }
            }
        }
    },

    beforeDestroy() {
        this.cancelThrottling();
        clearTimeout(this.notificationTimeout);
    },

    methods: {
        sendComment() {
            if (this.throttleAskingQuestions) {
                if (this.nrQuestionsAllowed <= 0) {
                    console.info('[Qna] Too many unmoderated questions, throttling');
                    this.showQnaLimitNotification = true;
                    clearTimeout(this.notificationTimeout);
                    this.notificationTimeout = setTimeout(() => this.showQnaLimitNotification = false, POST_NOTIFICATION_TIMEOUT);
                    return;
                }
                --this.nrQuestionsAllowed;
            }
            this.sending = true;

            this.api.customAction({
                newQuestion: this.newQuestion,
                anonymous: this.anonymousAllowed && this.sendAnonymously,
                onSuccess: () => this.onSendingSuccess(),
                onError: () => this.onSendingError()
            }, 'sideBarQna');
        },

        onSendingSuccess() {
            this.newQuestion = '';
            this.sending = false;
            if (!this.throttleAskingQuestions) {
                this.showPostNotification = true;
                clearTimeout(this.notificationTimeout);
                this.notificationTimeout = setTimeout(() => this.showPostNotification = false, POST_NOTIFICATION_TIMEOUT);
            }
        },

        onSendingError() {
            this.sending = false;
        },

        initThrottling() {
            this.resetThrottling();
            this.throttleInterval = setInterval(
                () => this.resetThrottling(),
                THROTTLE_TIME * 1000
            );
        },

        cancelThrottling() {
            clearInterval(this.timerTicker);
            clearInterval(this.throttleInterval);
        },

        /**
         * Resets the throttling and the timer
         */
        resetThrottling() {
            console.log('[Qna] Unthrottling');

            this.nrQuestionsAllowed = MAX_QUESTIONS_PER_THROTTLE;
            this.throttleTime = THROTTLE_TIME;

            clearInterval(this.timerTicker);
            this.timerTicker = setInterval(() => {
                if (this.throttleTime > 1) {
                    this.throttleTime--;
                }
            }, 1000);
        },

        onSortBySelected(sortBy: string) {
            this.api.customAction(sortBy, 'sideBarQnaSortBy');
        },

    }
});
